/**
 * The model choice as a settings control: the composer popover's rows
 * (modelRows) laid flat in a card instead of behind a trigger, because a
 * settings page has the room and nothing to type under it.
 *
 * Owner, 2026-09-02: the account and product defaults "should only show 3
 * options too just like chat model picker" and be "presented the same way".
 * So: an inherit row when the setting can defer to a wider one (the
 * product's "Use my account setting"), the three recommended tiers, and the
 * family list folded behind "All models". One difference from the popover,
 * on purpose: a value that IS a tier keeps the list folded, because the
 * ticked row is already on screen and unfolding five ids under it would put
 * the SKUs back. Only a value the tiers do not cover unfolds it, so the
 * ticked row is never hidden.
 *
 * A tier is a model AND an effort (labels.ModelTier), and picking one here
 * hands the caller both: the separate effort selects these pages used to
 * carry let a default drift to a pairing the bench never measured ("Best at
 * low"), so they are gone. A conversation still turns its own effort up or
 * down from the meter in its composer, which is where "dig deeper on this
 * one" belongs.
 */

import { useMemo, useState, type ReactNode } from 'react';

import { FIELD_WIDTH } from '../../lib/ui';
import type { Effort } from '../../api/types';
import { MODEL_TIERS, modelDisplayName, modelGroups, type ModelTier } from './labels';
import { AllModelsToggle, FamilyGroup, ModelRow, TierRows } from './modelRows';

/** Does a tier read as current: its model, and its effort when one is stored. */
export function tierIsCurrent(model: string | null, effort: Effort | null | undefined): boolean {
  return (
    model !== null &&
    MODEL_TIERS.some((t) => t.model === model && (effort === undefined || effort === t.effort))
  );
}

export function ModelTierSelect({
  model,
  effort,
  overridden = false,
  overriddenNote = null,
  inherit,
  onTier,
  onModel,
  onInherit,
  disabled = false,
  testId,
  ariaLabel,
  footnote,
}: {
  /** the stored value: a model id, or null while the inherit row is chosen */
  model: string | null;
  /** the stored effort beside it (omit where the setting has none) */
  effort?: Effort | null;
  /** something besides the model is set (an effort-only override): the
   * inherit row is not the truth then, and clears it */
  overridden?: boolean;
  /** what that something is, on the inherit row */
  overriddenNote?: string | null;
  /** the inherit row, for a setting that can defer to a wider one */
  inherit?: { label: string; resolvesTo: string | null };
  /** a tier row picked: its model and its effort, together */
  onTier: (tier: ModelTier) => void;
  /** a family-list row picked: the id alone */
  onModel: (model: string) => void;
  /** the inherit row picked while something was set */
  onInherit?: () => void;
  disabled?: boolean;
  testId: string;
  ariaLabel: string;
  /** the quiet last line: what this setting reaches */
  footnote?: ReactNode;
}) {
  const [showAll, setShowAll] = useState(false);
  const listOpen = showAll || (model !== null && !tierIsCurrent(model, effort));
  const groups = useMemo(() => modelGroups(model), [model]);

  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      data-testid={testId}
      className={`${FIELD_WIDTH} rounded-panel border border-default bg-raised p-1.5`}
    >
      {inherit !== undefined ? (
        <ModelRow
          role="radio"
          checked={model === null && !overridden}
          label={inherit.label}
          meta={
            model === null && overridden
              ? overriddenNote
              : inherit.resolvesTo === null
                ? null
                : modelDisplayName(inherit.resolvesTo)
          }
          testId={`${testId}-option-default`}
          tabIndex={0}
          disabled={disabled}
          onClick={() => {
            if (model !== null || overridden) onInherit?.();
          }}
        />
      ) : null}
      <TierRows
        model={model}
        effort={effort}
        testId={testId}
        role="radio"
        rowTabIndex={0}
        disabled={disabled}
        divider={inherit !== undefined}
        onPick={onTier}
      />
      <AllModelsToggle open={listOpen} onToggle={() => setShowAll((was) => !was)} testId={testId} />
      {listOpen
        ? groups.map((group) => (
            <FamilyGroup key={group.key} group={group}>
              {group.models.map((id) => (
                <ModelRow
                  key={id}
                  role="radio"
                  checked={id === model}
                  label={modelDisplayName(id)}
                  meta={null}
                  testId={`${testId}-option-${id}`}
                  tabIndex={0}
                  disabled={disabled}
                  onClick={() => {
                    if (id !== model) onModel(id);
                  }}
                />
              ))}
            </FamilyGroup>
          ))
        : null}
      {footnote !== undefined ? (
        <p className="mt-1 mb-0 border-t border-default px-2 pt-2 pb-1 text-[length:var(--text-micro-size)] leading-[var(--text-micro-line)] text-tertiary">
          {footnote}
        </p>
      ) : null}
    </div>
  );
}
